import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Home, 
  Shield, 
  Award, 
  Clock,
  Phone,
  CheckCircle,
  Star,
  Users,
  Wrench,
  FileText
} from 'lucide-react';

const ResidentialRoofing: React.FC = () => {
  const handleGetQuote = () => {
    window.open('https://carolinatrophyroofs.sky-quote.com/schedule#step1', '_blank');
  };

  const handleCallNow = () => {
    window.location.href = 'tel:252-371-2266';
  };

  const services = [
    {
      icon: Home,
      title: 'Roof Replacement',
      description: 'Complete roof replacement with premium materials and expert installation for maximum protection and curb appeal.'
    },
    {
      icon: Wrench,
      title: 'Roof Repair',
      description: 'Professional roof repairs for leaks, damaged shingles, and storm damage to extend your roof\'s lifespan.'
    },
    {
      icon: Shield,
      title: 'Storm Damage Restoration',
      description: 'Expert storm damage assessment and restoration services with direct insurance claim assistance.'
    },
    {
      icon: FileText,
      title: 'Roof Inspections',
      description: 'Comprehensive roof inspections for home purchases, insurance requirements, and preventive maintenance.'
    }
  ];

  const roofingMaterials = [
    {
      name: 'Asphalt Shingles',
      description: 'Most popular residential roofing choice offering durability, affordability, and variety.',
      lifespan: '20-30 years',
      warranty: 'Up to Lifetime'
    },
    {
      name: 'Architectural Shingles',
      description: 'Premium dimensional shingles providing enhanced curb appeal and superior protection.',
      lifespan: '25-50 years',
      warranty: 'Up to Lifetime'
    },
    {
      name: 'Metal Roofing',
      description: 'Long-lasting, energy-efficient option perfect for North Carolina weather conditions.',
      lifespan: '40-70 years',
      warranty: '30-50 years'
    },
    {
      name: 'Slate & Tile',
      description: 'Premium natural materials offering unmatched durability and distinctive appearance.',
      lifespan: '50-100+ years',
      warranty: '50+ years'
    }
  ];

  const serviceAreas = [
    'Rocky Mount, NC',
    'Greenville, NC', 
    'Wilson, NC',
    'Goldsboro, NC',
    'Raleigh, NC',
    'Jacksonville, NC',
    'Nash County',
    'Edgecombe County'
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: 'Licensed & Insured',
      description: 'Fully licensed residential roofing contractor with comprehensive insurance coverage.'
    },
    {
      icon: Star,
      title: '5-Star Reviews',
      description: 'Consistently rated 5 stars by homeowners across Eastern North Carolina.'
    },
    {
      icon: Users,
      title: 'Local Experts',
      description: 'Deep understanding of North Carolina building codes and weather patterns.'
    },
    {
      icon: Clock,
      title: '24/7 Emergency',
      description: 'Emergency roof repair services available around the clock for urgent situations.'
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-charcoal-gray to-gray-800 text-white">
          <div className="max-w-7xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 px-4 py-2 text-sm font-medium border-brand-trophy-gold text-brand-trophy-gold">
              <Home className="w-4 h-4 mr-2" />
              Residential Roofing Specialists
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Residential Roofing
              <span className="text-brand-trophy-gold block mt-2">Eastern North Carolina</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
              Professional residential roofing services for homeowners in Rocky Mount, Greenville, Wilson, 
              and surrounding Eastern NC areas. From roof replacement to emergency repairs, we protect your home with quality craftsmanship.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={handleGetQuote}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-3"
              >
                Get Free Estimate
              </Button>
              <Button 
                onClick={handleCallNow}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-3"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call (252) 371-2266
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center max-w-2xl mx-auto">
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">15+</div>
                <div className="text-sm text-gray-300">Years Experience</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">5000+</div>
                <div className="text-sm text-gray-300">Homes Protected</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">24/7</div>
                <div className="text-sm text-gray-300">Emergency Service</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">100%</div>
                <div className="text-sm text-gray-300">Satisfaction</div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Comprehensive Residential Roofing Services
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                From minor repairs to complete roof replacements, we provide all the residential roofing services your home needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="w-12 h-12 text-brand-trophy-gold mx-auto mb-4" />
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Roofing Materials */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Premium Residential Roofing Materials
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We install only the highest quality roofing materials from trusted manufacturers, backed by comprehensive warranties.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {roofingMaterials.map((material, index) => (
                <Card key={index} className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-xl text-brand-charcoal-gray">{material.name}</CardTitle>
                    <CardDescription>{material.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <span className="text-sm text-gray-500">Lifespan:</span>
                        <div className="font-semibold">{material.lifespan}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Warranty:</span>
                        <div className="font-semibold">{material.warranty}</div>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" asChild>
                      <Link to="/contact">Learn More</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose Carolina Trophy Roofs?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Experience the difference of working with Eastern NC's most trusted residential roofing contractor.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((feature, index) => (
                <div key={index} className="text-center">
                  <feature.icon className="w-16 h-16 text-brand-trophy-gold mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Serving Eastern North Carolina
            </h2>
            <p className="text-lg text-gray-600 mb-12">
              Providing professional residential roofing services throughout Eastern NC
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-brand-trophy-gold mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-700">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-charcoal-gray text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Protect Your Home?
            </h3>
            <p className="text-xl text-gray-300 mb-8">
              Get a free estimate for your residential roofing project. Our experts are standing by to help protect your most valuable investment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleGetQuote}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-3"
              >
                Get Free Estimate
              </Button>
              <Button 
                onClick={handleCallNow}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-3"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: (252) 371-2266
              </Button>
            </div>
          </div>
        </section>

        {/* Reference Sources */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Authoritative Residential Roofing Resources
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'National Roofing Contractors Association', url: 'https://www.nrca.net/homeowner-resources' },
                { name: 'Better Business Bureau - Roofing Tips', url: 'https://www.bbb.org/article/tips/13747-bbb-tip-hiring-a-roofing-contractor' },
                { name: 'International Association of Certified Home Inspectors', url: 'https://www.nachi.org/roof-inspection.htm' },
                { name: 'Consumer Reports - Roofing Guide', url: 'https://www.consumerreports.org/home-garden/roofing/' },
                { name: 'NC Department of Insurance', url: 'https://www.ncdoi.gov/consumers' },
                { name: 'Energy Star - Cool Roofs', url: 'https://www.energystar.gov/products/building_products/roof_products' },
                { name: 'Federal Trade Commission - Home Improvements', url: 'https://www.consumer.ftc.gov/articles/hiring-contractor' },
                { name: 'North Carolina Attorney General - Consumer Protection', url: 'https://ncdoj.gov/protecting-consumers/' }
              ].map((ref, index) => (
                <a 
                  key={index}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-brand-trophy-gold hover:shadow-md transition-all"
                >
                  <div className="text-sm font-medium text-brand-charcoal-gray hover:text-brand-trophy-gold">
                    {ref.name}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ResidentialRoofing;